import { Directive, ElementRef, OnDestroy, inject, input } from '@angular/core';

@Directive({
  selector: '[appTooltip]',
  host: {
    '(mouseenter)': 'scheduleShow()',
    '(mouseleave)': 'scheduleHide()',
    '(focusin)': 'scheduleShow()',
    '(focusout)': 'scheduleHide()',
  },
})
export class UiTooltip implements OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly appTooltip = input('');
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private tip: HTMLDivElement | null = null;
  private readonly onReposition = () => this.removeNow();

  scheduleShow(): void {
    this.clearHideTimer();
    if (this.tip) {
      return;
    }
    this.clearShowTimer();
    const text = this.appTooltip().trim();
    if (!text) {
      return;
    }
    this.showTimer = setTimeout(() => this.show(text), 180);
  }

  scheduleHide(): void {
    this.clearShowTimer();
    if (!this.tip) {
      return;
    }
    this.tip.classList.add('is-out');
    this.hideTimer = setTimeout(() => this.removeNow(), 120);
  }

  hide(): void {
    this.scheduleHide();
  }

  ngOnDestroy(): void {
    this.removeNow();
  }

  private show(text: string): void {
    this.removeNow();
    const el = document.createElement('div');
    el.className = 'ui-tooltip';
    el.setAttribute('role', 'tooltip');
    el.textContent = text;
    document.body.appendChild(el);
    this.tip = el;
    this.place(el);
    requestAnimationFrame(() => el.classList.add('is-in'));
    window.addEventListener('scroll', this.onReposition, true);
    window.addEventListener('resize', this.onReposition);
  }

  private place(el: HTMLDivElement): void {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const width = el.offsetWidth;
    const height = el.offsetHeight;
    const gap = 10;
    const left = Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8);
    let top = rect.top - height - gap;
    let placement = 'top';
    if (top < 8) {
      top = rect.bottom + gap;
      placement = 'bottom';
    }
    const arrow = Math.min(Math.max(12, rect.left + rect.width / 2 - left), width - 12);
    el.dataset['placement'] = placement;
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    el.style.setProperty('--arrow-left', `${Math.round(arrow)}px`);
  }

  private removeNow(): void {
    this.clearShowTimer();
    this.clearHideTimer();
    window.removeEventListener('scroll', this.onReposition, true);
    window.removeEventListener('resize', this.onReposition);
    this.tip?.remove();
    this.tip = null;
  }

  private clearShowTimer(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
